import type { UserResponseSchema } from '@/types/user.types';
import { MediaBaseResponse, SortDirection } from './types';

export enum VendorStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
}

// ==================== Document Types ====================

export interface VendorDocument {
  vendorDocumentId?: string;
  name: string;
  mediaId: string;
  vendorId?: string | null;
  media?: MediaBaseResponse | null;
  createdById: string;
  sortOrder?: number;
  orderBy?: number;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
  fileName?: string | null;
}

export interface VendorDocumentCreateRequest {
  name: string;
  mediaId: string;
}

export interface VendorDocumentUpdateRequest {
  vendorDocumentId: string;
  name?: string;
  mediaId?: string;
}

export interface VendorDocumentDeleteRequest {
  vendorDocumentId: string;
}

// ==================== Contact Types ====================

export interface VendorContact {
  vendorContactId?: string;
  vendorId?: string | null;
  firstName: string;
  lastName: string;
  status: VendorStatus;
  contactName?: string | null;
  email: string;
  phone?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

export interface VendorContactCreateRequest {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  phone?: string | null;
}

export interface VendorContactUpdateRequest {
  vendorContactId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
}

// ==================== Query & Filter Types ====================

export interface VendorQueryParams {
  pageNo?: number;
  pageSize?: number;
  sortField?: 'name' | 'city' | 'state' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  name?: string;
}

// ==================== Request Types ====================

export interface VendorCreateRequest {
  name: string;
  address1: string;
  address2?: string | null;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  taxId: string;
  fees: number;
  notes?: string | null;
  coverageAreas: string[];
  counties?: string[];
  documents?: VendorDocumentCreateRequest[];
  contacts?: VendorContactCreateRequest[];
}

export interface VendorPatchUpdateRequestDocuments {
  create?: VendorDocumentCreateRequest[];
  update?: VendorDocumentUpdateRequest[];
  delete?: VendorDocumentDeleteRequest[];
}

export interface VendorPatchUpdateRequest {
  name?: string;
  address1?: string;
  address2?: string | null;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  email?: string;
  taxId?: string;
  fees?: number;
  notes?: string | null;
  coverageAreas?: string[];
  counties?: string[];
  documents?: VendorPatchUpdateRequestDocuments;
  vendorContacts?: {
    create?: VendorContactCreateRequest[];
    update?: VendorContactUpdateRequest[];
  };
}

// ==================== Response Types ====================

export interface VendorResponse {
  vendorId: string;
  name: string;
  address1: string;
  address2?: string | null;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  taxId: string;
  fees: number;
  status: VendorStatus;
  notes?: string | null;
  coverageAreas: string[];
  counties: string[];
  documents: VendorDocument[];
  vendorContacts: VendorContact[];
  createdById: string;
  deactivatedAt?: string | null;
  deactivatedBy?: UserResponseSchema | null;
  createdAt: string;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

export interface VendorGetAllResponse {
  vendors: VendorResponse[];
  totalRows: number;
  totalPages: number;
  currentPage: number;
}

export interface VendorCreateResponse {
  message: string;
  data: VendorResponse;
}

export interface VendorGenericResponse {
  message?: string;
}

export interface VendorUpdateResponse {
  message: string;
}

export interface VendorCSVExportResponse {
  downloadUrl: string;
}

// ==================== CSV Export Types ====================

export interface VendorCSVRecord {
  'Vendor Name': string;
  'Vendor Email': string;
  'Phone Number': string;
  'Vendor Status': string;
  Address: string;
  City: string;
  State: string;
  'Zip Code': string;
  'Tax ID': string;
  'Coverage area(s)': string;
  Notes: string;
  'Vendor Fees': number;
}

// ==================== Form Types (for React components) ====================

export interface VendorFormData {
  name: string;
  address1: string;
  address2: string;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  taxId: string;
  fees: number | string;
  notes: string;
  coverageAreas: string[];
  documents: File[] | VendorDocument[];
  contacts: VendorContactCreateRequest[];
}

export interface VendorEditFormData extends VendorFormData {
  vendorId: string;
  status: VendorStatus;
  documents: VendorDocument[];
  contacts: VendorContact[];
}

export interface VendorGetAllParams {
  pageNo?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: SortDirection;
  name?: string;
  coverageArea?: string;
  status?: VendorStatus;
  search?: string;
}

export interface VendorContactGetAllParams {
  search?: string;
}
